var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useEffect, useMemo, useState } from 'react';
import { useAccount, useSwitchChain, useConnect, useDisconnect, useEnsName, useEnsAvatar } from 'wagmi';
import { isEIP6963Connector } from '../utils/isEIP6963Connector.js';
import { getWalletConnectUri } from '../utils/getWalletConnectUri.js';
import { isMobile, WalletType } from '@usecapsule/react-sdk';
import { normalize } from 'viem/ens';
export const defaultEvmExternalWallet = {
    wallets: [],
    chains: [],
    chainId: undefined,
    username: undefined,
    avatar: undefined,
    disconnect: () => Promise.resolve(),
    switchChain: () => Promise.resolve({}),
};
export const EvmExternalWalletContext = createContext(defaultEvmExternalWallet);
export function EvmExternalWalletProvider({ children, capsule, onSwitchWallet }) {
    const { connectAsync, connectors: untypedConnectors } = useConnect();
    const { address: wagmiAddress, isConnecting, isReconnecting, chainId, connector: connectedConnector } = useAccount();
    const { chains, switchChainAsync } = useSwitchChain();
    const { disconnectAsync } = useDisconnect();
    const { data: ensName } = useEnsName({ address: wagmiAddress });
    const { data: ensAvatar } = useEnsAvatar({
        name: normalize(ensName),
    });
    const [isLocalConnecting, setIsLocalConnecting] = useState(false);
    useEffect(() => {
        const storedExternalWallet = capsule.externalWallets[wagmiAddress !== null && wagmiAddress !== void 0 ? wagmiAddress : ''];
        if (!isConnecting && !isReconnecting && !isLocalConnecting && !!wagmiAddress && !storedExternalWallet) {
            reset();
        }
    }, [isConnecting, isReconnecting]);
    useEffect(() => {
        var _a, _b;
        const storedExternalWallet = capsule.externalWallets[(_b = (_a = capsule.currentExternalWalletAddresses) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : ''];
        // If the user is using an external EVM wallet we want to watch for wallet changes and log them in to a different user when the wallet changes
        if (!isConnecting &&
            !isReconnecting &&
            (storedExternalWallet === null || storedExternalWallet === void 0 ? void 0 : storedExternalWallet.type) === WalletType.EVM &&
            (storedExternalWallet === null || storedExternalWallet === void 0 ? void 0 : storedExternalWallet.address) !== wagmiAddress) {
            switchWallet(wagmiAddress);
        }
    }, [wagmiAddress, isReconnecting, isConnecting]);
    const connectors = untypedConnectors;
    const reset = () => __awaiter(this, void 0, void 0, function* () {
        yield disconnectAsync();
        yield capsule.logout(true);
    });
    const switchChain = (chainId) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        let error;
        try {
            yield switchChainAsync({ chainId });
        }
        catch (e) {
            // Invalid networks throws the same error as connection rejected requests, so catching the error using the detail string here.
            if (e.details.includes('Missing or invalid.')) {
                const chain = chains.find(c => c.id === chainId);
                error = [
                    'Network not supported.',
                    `You may need to add ${chain === null || chain === void 0 ? void 0 : chain.name} support to ${(_c = (_b = (_a = connectedConnector === null || connectedConnector === void 0 ? void 0 : connectedConnector.capsuleDetails) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : connectedConnector === null || connectedConnector === void 0 ? void 0 : connectedConnector.name) !== null && _c !== void 0 ? _c : 'the wallet'} manually.`,
                ];
            }
            else {
                switch (e.name) {
                    case 'UserRejectedRequestError': {
                        error = ['Change request rejected'];
                        break;
                    }
                    default: {
                        error = ['An unknown error occurred'];
                        break;
                    }
                }
            }
        }
        return { error };
    });
    const login = (address, connectorName) => __awaiter(this, void 0, void 0, function* () {
        try {
            yield capsule.externalWalletLogin(address, WalletType.EVM, connectorName);
        }
        catch (err) {
            yield disconnectAsync();
            yield capsule.logout(true);
            throw 'Error logging you in. Please try again.';
        }
    });
    const switchWallet = (address) => __awaiter(this, void 0, void 0, function* () {
        setIsLocalConnecting(true);
        let error;
        // If we're calling switch wallet with no address, treat it as if the user disconnected the wallet from the app and logout to reset the Capsule instance.
        if (!address) {
            yield capsule.logout(true);
        }
        else {
            try {
                yield login(address, connectedConnector === null || connectedConnector === void 0 ? void 0 : connectedConnector.name);
            }
            catch (err) {
                error = err;
            }
        }
        onSwitchWallet({ address, error });
        setIsLocalConnecting(false);
    });
    const connect = (connector) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        setIsLocalConnecting(true);
        yield disconnectAsync();
        const walletChainId = yield connector.getChainId();
        let address;
        let error;
        try {
            const data = yield connectAsync({
                // If the wallet is already on a supported chain, use that to avoid a chain switch prompt.
                chainId: (_b = (_a = chains.find(({ id }) => id === walletChainId)) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : 
                // Fall back to the first chain provided.
                (_c = chains[0]) === null || _c === void 0 ? void 0 : _c.id,
                connector,
            });
            address = (_d = data.accounts) === null || _d === void 0 ? void 0 : _d[0];
            if (address) {
                try {
                    yield login(address, connector.name);
                }
                catch (err) {
                    address = undefined;
                    error = err;
                }
            }
        }
        catch (e) {
            switch (e.name) {
                case 'UserRejectedRequestError': {
                    error = 'Connection request rejected';
                    break;
                }
                case 'ResourceUnavailableRpcError': {
                    `${connector.name} not detected`;
                    break;
                }
                default: {
                    error = 'An unknown error occurred';
                    break;
                }
            }
        }
        setIsLocalConnecting(false);
        return { address, error };
    });
    const connectMobile = (connector) => __awaiter(this, void 0, void 0, function* () {
        // If on mobile and the connector contains the wallet connect modal connector, use it.
        const _connector = connector.walletConnectModalConnector && isMobile() ? connector.walletConnectModalConnector : connector;
        return yield connect(_connector);
    });
    const getQrUri = (connector) => () => {
        var _a;
        return getWalletConnectUri(connector, (_a = connector.capsuleDetails) === null || _a === void 0 ? void 0 : _a.getUri);
    };
    // If an Eip6963 wallet is injected we want to remove the non Eip6963 connector and attach its metadata to the Eip6963 connector
    const nonEip6963ConnectorsByRdns = {};
    let walletConnectModalConnector;
    connectors
        .filter(c => !isEIP6963Connector(c))
        .forEach(c => {
        if (c.capsuleDetails) {
            nonEip6963ConnectorsByRdns[c.capsuleDetails.rdns] = c.capsuleDetails;
            if (c.capsuleDetails.isWalletConnectModalConnector) {
                walletConnectModalConnector = c;
            }
        }
    });
    const eip6963ids = connectors.filter(c => isEIP6963Connector(c)).map(c => c.id);
    const dedupedConnectors = connectors
        .map(c => {
        var _a, _b, _c;
        // Filter out the duplicated walletConnect connector with the modal
        // This connector will be attached to the WC connector that doesn't contain the modal for use on mobile
        if ((_a = c.capsuleDetails) === null || _a === void 0 ? void 0 : _a.isWalletConnectModalConnector) {
            return;
        }
        // Remove any non EIP6963 connectors if they have a matching EIP6963 connectors
        if (!isEIP6963Connector(c) && eip6963ids.includes((_b = c.capsuleDetails) === null || _b === void 0 ? void 0 : _b.rdns)) {
            return;
        }
        // Return the EIP6963 connectors
        if (isEIP6963Connector(c)) {
            const capsuleMetadata = nonEip6963ConnectorsByRdns[c.id];
            return Object.assign(Object.assign({}, c), { capsuleDetails: capsuleMetadata });
        }
        // Return the WC connector with the attached WC modal connector
        if (((_c = c.capsuleDetails) === null || _c === void 0 ? void 0 : _c.id) === 'walletConnect' && walletConnectModalConnector) {
            return Object.assign(Object.assign({}, c), { walletConnectModalConnector });
        }
        return c;
    })
        .filter(c => !!c);
    const wallets = dedupedConnectors.map(c => {
        const connector = Object.assign(Object.assign({}, c), c.capsuleDetails);
        return Object.assign(Object.assign({}, connector), { connect: () => connect(connector), connectMobile: () => connectMobile(connector), type: WalletType.EVM, getQrUri: getQrUri(connector) });
    });
    const formattedChains = chains.map(c => {
        return {
            id: c.id,
            name: c.name,
        };
    });
    const username = useMemo(() => ensName !== null && ensName !== void 0 ? ensName : wagmiAddress, [ensName, wagmiAddress]);
    const disconnect = disconnectAsync;
    return (_jsx(EvmExternalWalletContext.Provider, { value: useMemo(() => ({ wallets, chains: formattedChains, chainId, username, avatar: ensAvatar, disconnect, switchChain }), [wallets, formattedChains, chainId, username, ensAvatar, disconnect, switchChain]), children: children }));
}
